import { SelectOption, SwappableDialogRef } from '@common/types/common';
import { SuccessDialogContent } from '@components/display/dialogs';
import { CloseButton } from '@components/utility/buttons';
import { ChipSelector } from '@components/utility/chips';
import { Tabs } from '@components/utility/tabs';
import {
    useCreateEventRequiredBadgeMutation,
    useCreateEventRewardedBadgeMutation,
    useGetBadgesAndLevelsOptionsQuery,
} from '@event/services/events';
import { EventTypes } from '@event/types';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutline';
import { Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog/Dialog';
import { handleError, toaster } from '@utils/common';
import { clsx } from 'clsx';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const TimeframeOptions = [
    { label: '1 Year', value: 'P1Y' },
    { label: '2 Years', value: 'P2Y' },
    { label: '3 Years', value: 'P3Y' },
    { label: 'No requirement', value: '' },
];

type Screens = 'main' | 'success';

const AddBadgeDialog = forwardRef<
    SwappableDialogRef & { setTab: (tab: 'rewarded' | 'required') => void },
    Omit<DialogProps, 'children'>
>(({ ...props }, ref) => {
    const [screen, setScreen] = useState<Screens>('main');

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const isPrivate = useMemo(
        () => [EventTypes.PRIVATE_ONSITE, EventTypes.PRIVATE_VIRTUAL].includes(eventType),
        [eventType]
    );
    const [tab, setTab] = useState(isPrivate ? 'rewarded' : 'required');
    const [solutions, setSolutions] = useState<string[]>([]);
    const [levelAndTimeframe, setLevelAndTimeframe] = useState<{ level: string | null; timeframe: string }>({
        level: null,
        timeframe: '',
    });

    const { data: badgesAndLevelsOptions, isLoading: isLoadingOptions } = useGetBadgesAndLevelsOptionsQuery();
    const badgeOptions = badgesAndLevelsOptions?.badges || [];
    const levelOptions = badgesAndLevelsOptions?.levels || [];

    const hasMissingInfo = useMemo(() => {
        if (!levelAndTimeframe.level || !solutions.length) return true;
        return false;
    }, [levelAndTimeframe, solutions, tab]);

    const [createEventRequiredBadge, { isLoading: isLoadingRequired }] = useCreateEventRequiredBadgeMutation();
    const [createEventRewardedBadge, { isLoading: isLoadingRewarded }] = useCreateEventRewardedBadgeMutation();

    const isLoading = isLoadingRequired || isLoadingRewarded || isLoadingOptions;

    const swapScreen = (screenName: Screens) => setScreen(screenName);

    useImperativeHandle(ref, () => ({
        setTab,
        swapScreen,
    }));

    const handleSubmit = async () => {
        if (hasMissingInfo) return;

        try {
            if (tab === 'required') {
                await createEventRequiredBadge({
                    type: eventType,
                    id: Number(eventId),
                    badges: solutions.map((solution) => ({
                        level_id: Number(levelAndTimeframe.level),
                        badge_id: Number(solution),
                        timeframe: levelAndTimeframe.timeframe || '',
                    })),
                }).unwrap();
            } else {
                await createEventRewardedBadge({
                    type: eventType,
                    id: Number(eventId),
                    badges: solutions.map((solution) => ({
                        level_id: Number(levelAndTimeframe.level),
                        badge_id: Number(solution),
                    })),
                }).unwrap();
            }
            toaster.success({
                msg: 'Badge added successfully',
            });
            swapScreen('success');
        } catch (error) {
            handleError('There was an error adding the badge', error);
        }
    };

    const handleAddMoreBadges = () => {
        setLevelAndTimeframe({ level: null, timeframe: '' });
        setSolutions([]);
        swapScreen('main');
    };

    const handleClose = () => {
        props.onClose?.({}, 'backdropClick');
        setTimeout(() => {
            setLevelAndTimeframe({ level: null, timeframe: '' });
            setSolutions([]);
            setTab(isPrivate ? 'rewarded' : 'required');
            swapScreen('main');
        }, 300);
    };

    const handleSolutionChange = ({ value: solution }: SelectOption<string>) => {
        if (solutions.findIndex((s) => s === solution) !== -1) {
            setSolutions(solutions.filter((s) => s !== solution));
        } else {
            setSolutions((prev) => [...prev, solution]);
        }
    };

    return (
        <Dialog
            {...props}
            onClose={handleClose}
            classes={{ paper: 'min-w-0 max-w-[900px] rounded-2xl pt-14 pb-16 px-20' }}
        >
            {screen === 'main' ? (
                <>
                    <DialogTitle align="center">
                        <Typography fontSize={32} fontFamily="museo-sans" letterSpacing={-0.8}>
                            Add a Badge
                        </Typography>
                        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
                            <Tabs.Tab className={clsx({ hidden: isPrivate })} label="Required Badge" value="required" />
                            <Tabs.Tab label="Rewarded Badges" value="rewarded" />
                        </Tabs>
                    </DialogTitle>
                    <DialogContent>
                        <CloseButton onClick={handleClose} />
                        <ChipSelector
                            title="Training Level"
                            className="mt-5"
                            chips={levelOptions}
                            value={levelAndTimeframe.level !== null ? [levelAndTimeframe.level] : []}
                            onChipClick={(chip) => setLevelAndTimeframe((prev) => ({ ...prev, level: chip.value }))}
                        />
                        <ChipSelector
                            title="Victron Solutions"
                            className="mt-5"
                            chips={badgeOptions}
                            value={solutions}
                            onChipClick={handleSolutionChange}
                            selector="checkbox"
                        />
                        {tab === 'required' ? (
                            <ChipSelector
                                title="Timeframe Validation"
                                className="mt-5"
                                chips={TimeframeOptions}
                                value={levelAndTimeframe.timeframe !== null ? [levelAndTimeframe.timeframe] : []}
                                onChipClick={(chip) =>
                                    setLevelAndTimeframe((prev) => ({ ...prev, timeframe: chip.value }))
                                }
                            />
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={hasMissingInfo || isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleOutlinedIcon />}
                            fullWidth
                            className="black-border rounded-full"
                            onClick={handleSubmit}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <SuccessDialogContent
                    description="Badge has been successfully added."
                    secondaryBtn={{
                        text: 'Add More Badges',
                        onClick: handleAddMoreBadges,
                    }}
                    btn={{
                        text: 'Continue',
                        onClick: handleClose,
                    }}
                />
            )}
        </Dialog>
    );
});

export { AddBadgeDialog };
